import React, { useState, useEffect, useRef, useCallback, useMemo, type MouseEvent } from 'react';
import * as R from 'ramda';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { JiraBottomRightCornerOutlet } from '@atlassian/jira-layout-controller/src/ui/bottom-right-corner/outlet/index.tsx';
import Spinner from '@atlassian/jira-portfolio-3-common/src/spinner/index.tsx';
import { PRODUCT_ANALYTICS_EVENT_NAMES } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/analytics/types.tsx';
import { GroupBy } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/ui/main/tabs/dependencies/filters/types.tsx';
import { ReportStatus } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/ui/main/tabs/dependencies/report-state/types.tsx';
import type { StagePosAndScale } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/ui/main/tabs/dependencies/stage-position-scale/types.tsx';
import ViewExperienceFailureTrackerComponent from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/failure-tracker/index.tsx';
import { fireUIAnalytics, MountEvent } from '@atlassian/jira-product-analytics-bridge';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import ConnectionsComponent from './connections/index.tsx';
import FilterQueryParams from './filter-query-params/index.tsx';
import FiltersComponent from './filters/index.tsx';
import EpicClusterComponent from './groups/epic/index.tsx';
import ProjectClusterComponent from './groups/project/index.tsx';
import SprintClusterComponent from './groups/sprint/index.tsx';
import TeamClusterComponent from './groups/team/index.tsx';
import IssueViewComponent from './issue/index.tsx';
import Legend from './legend/index.tsx';
import OtherIssueComponent from './other-issues/index.tsx';
import StateScreen from './state-screen/index.tsx';
import { ScreenType } from './state-screen/types.tsx';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from './styles.module.css';
import TeamViewComponent from './team-item/index.tsx';
import { NO_PARENT_GROUP_SUFFIX, OTHER_GROUP_ID, type Props } from './types.tsx';
import UseLocalStorageCommand from './use-local-storage-command/index.tsx';
import {
	layoutIssues,
	calculateFitPosition,
	nextZoomStep,
	calculatePositionalZoom,
	getGroupIdFromGroupKey,
} from './utils.tsx';
import ZoomBar from './zoom-bar/index.tsx';

const zoomSteps = [0.1, 0.25, 0.5, 0.75, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2, 2.5, 3];

function Dependencies({
	issuesById,
	dependencyLinks: { links: dependencyLinks, otherIssues, activeIssue, hasDependency },
	issueLinkTypesById,
	focusIssueLink,
	projectsById,
	teamsById,
	sprintsById,
	groupBy,
	rollupBy,
	IssueView = IssueViewComponent,
	TeamView = TeamViewComponent,
	OtherIssue = OtherIssueComponent,
	TeamCluster = TeamClusterComponent,
	ProjectCluster = ProjectClusterComponent,
	EpicCluster = EpicClusterComponent,
	Connections = ConnectionsComponent,
	Filters = FiltersComponent,
	onDependenciesView,
	mode,
	onLineClick,
	onCardClick,
	syncStartEnabled,
	issueStatusesById,
	setIssueFilter,
	initialStagePosAndScale,
	setStagePositionAndScale,
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	onExperienceSuccess = () => {},
	ViewExperienceFailureTracker = ViewExperienceFailureTrackerComponent,
	reportTimeToInteractive,
	isLoading,
	legendToggleButton,
	setReportStatus,
	isSmartLink,
}: Props) {
	const intl = useIntl();
	const locale = useLocale();

	useEffect(() => {
		onExperienceSuccess();
	}, [onExperienceSuccess]);

	// analytics and performance monitoring
	useEffect(() => {
		if (!isLoading) {
			reportTimeToInteractive({
				isNewSidebarEnabled: true,
			});
		}
	}, [isLoading, reportTimeToInteractive]);

	// A short tale about this condition ...
	// Imagine a plan where an issue A blocks an issue B.
	// If the dependencies visualization is filtered by Issue A (i.e. "activeIssue = "<issueA_id>")
	// and the user removes Issue B from the dependencies of Issue A in the Roadmap tab,
	// then the "dependencyLinks" object becomes empty "{}", however "activeIssue" is still set to <issueA_id>
	// therefore the dependencies visualization crashes since there are no dependency links for Issue A
	// ==> we reset the "Filter by issue" search field so the dependencies visualization re-renders
	if (!Object.keys(dependencyLinks).length && activeIssue) {
		setIssueFilter(null);
		// NB: if "dependencyLinks === {}" and "activeIssue === null", then there are no issues with dependencies in the plan
		// so there is no point in resetting the "Filter by issue" search field - it's already empty
	}

	const containerRef = useRef<HTMLDivElement>(null);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const wrapperRect = useRef<ClientRect | null>(null);
	const shouldMoveStage = useRef<boolean>(false);
	const zoomBarZoomSetter = useRef<((zoom: number) => void) | null>(null);
	const [currentDetailFlyoutId, setCurrentDetailFlyoutId] = useState<string | null>(null);

	// This ref holds the values for the stage position and zoom level
	// We are NOT using state for these values since these values are not updated through react but instead we directly change the value in the DOM node
	// The reason for this is performance. Even with few nodes, going through react will not be a pleasant experience
	const stagePosAndScale = useRef<StagePosAndScale>({
		...initialStagePosAndScale,
	});

	// when unmount the component store the stagePos to redux
	useEffect(
		() => () => {
			// stagePosAndScale.current just refer to an object and not a node, so we could make sure it will not be changed by react
			setStagePositionAndScale({ ...(stagePosAndScale && stagePosAndScale.current) });
		},
		[setStagePositionAndScale],
	);

	// A setter for the scale in the stagePosAndScale ref.
	// This allows us to call the zoomBarZoomSetter whenever the scale changes, which is how we can force
	// the ZoomBar to rerender without rerendering this parent component.
	// zoomBarZoomSetter holds a state setter that belongs to the ZoomBar, which we get access to via the
	// ZoomBar's onMount prop.
	const setScale = useCallback((newScale: number) => {
		stagePosAndScale.current.scale = newScale;
		if (zoomBarZoomSetter.current) {
			zoomBarZoomSetter.current(newScale);
		}
	}, []);

	const { status, graph, labels, groupsOfIssues, itemIdsForGraph, otherIssuesInGroupMap } = useMemo(
		() =>
			layoutIssues(
				intl,
				isLoading,
				issuesById,
				dependencyLinks,
				issueLinkTypesById,
				groupBy,
				teamsById,
				rollupBy,
				otherIssues,
				syncStartEnabled,
				issueStatusesById,
				sprintsById,
				locale,
			),
		[
			intl,
			isLoading,
			issuesById,
			dependencyLinks,
			issueLinkTypesById,
			groupBy,
			teamsById,
			rollupBy,
			otherIssues,
			syncStartEnabled,
			issueStatusesById,
			sprintsById,
			locale,
		],
	);

	setReportStatus(status);

	const layout = graph.graph();

	const getFitPosition = useCallback((): StagePosAndScale => {
		const wRect = wrapperRect.current;
		if (wRect && layout.width && layout.height) {
			return calculateFitPosition(wRect, layout.width, layout.height);
		}

		return {
			x: 0,
			y: 0,
			scale: 1,
		};
	}, [layout]);

	const getNextZoomStep = (zoomingIn = true) =>
		nextZoomStep(zoomSteps, stagePosAndScale.current.scale, zoomingIn);

	const updateStagePos = useCallback(
		(x: number, y: number, nextScale: number = stagePosAndScale.current.scale) => {
			stagePosAndScale.current.x = x;
			stagePosAndScale.current.y = y;

			if (typeof nextScale === 'number') {
				setScale(nextScale);
			}

			if (containerRef.current) {
				containerRef.current.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${stagePosAndScale.current.scale})`;
			}
			// trigger scroll event manually so the popup component can recalculate its position

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			document.body.dispatchEvent(new CustomEvent('scroll'));
		},
		[setScale],
	);

	const zoom = useCallback(
		(newScaleValue: number, mouseX?: number, mouseY?: number) => {
			if (!wrapperRect.current) {
				return;
			}

			const wRect: ClientRect = wrapperRect.current;
			// Restrict scale. The maximum a user can zoom out is 0.1 (or the tightest zoom that fits all issues)
			const minZoom = Math.min(zoomSteps[0], getFitPosition().scale);
			const nextScale = Math.max(minZoom, newScaleValue);
			const newStagePos = calculatePositionalZoom(
				nextScale,
				stagePosAndScale.current,
				wRect,
				mouseX,
				mouseY,
			);

			updateStagePos(newStagePos.x, newStagePos.y, newStagePos.scale);
		},
		[getFitPosition, updateStagePos],
	);

	// Zoom on mouse wheel
	useEffect(() => {
		const handleWheel = (event: WheelEvent) => {
			event.preventDefault();
			event.stopPropagation();
			// the multiplication factor (-0.001) eases the zoom
			const nextScale = stagePosAndScale.current.scale + event.deltaY * -0.001;
			zoom(nextScale, event.clientX, event.clientY);
		};

		const wrapper = wrapperRef.current;

		if (wrapper) {
			wrapper.addEventListener('wheel', handleWheel, {
				passive: false,
			});
		}
		return () => {
			if (wrapper) {
				wrapper.removeEventListener('wheel', handleWheel);
			}
		};
	}, [zoom]);

	// Update wrapperRect on window resize and when issues finished loading
	const updateWrapperHeight = () => {
		const handleWindowResize = () => {
			if (wrapperRef.current) {
				wrapperRect.current = wrapperRef.current.getBoundingClientRect();
			}
		};

		const wrapper = wrapperRef.current;

		if (wrapper) {
			const wRect = wrapper.getBoundingClientRect();
			wrapperRect.current = wRect;

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.addEventListener('resize', handleWindowResize);
			// Dynamically set the wrapper height to extend to the bottom
			wrapper.style.height = `calc(100vh - ${wRect.top}px)`;
		}
		return () => {
			if (wrapper) {
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window.removeEventListener('resize', handleWindowResize);
			}
		};
	};

	useEffect(() => {
		updateWrapperHeight();
	}, [isLoading]);

	useEffect(() => {
		onDependenciesView(mode);
	}, [mode, onDependenciesView]);

	const [newDependency, setNewDependency] = useState<string | null>(null);
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const lastGraph = useRef<any | null>(graph);
	const panToNewDependency = useCallback(() => {
		if (newDependency) {
			const nodePos = graph.node(newDependency);
			if (nodePos) {
				setNewDependency(null);
				updateStagePos(
					(-nodePos.x + nodePos.width / 2) * stagePosAndScale.current.scale,
					(-nodePos.y + nodePos.height / 2) * stagePosAndScale.current.scale,
					stagePosAndScale.current.scale,
				);
				focusIssueLink(newDependency);
			}
		}
	}, [newDependency, focusIssueLink, graph, updateStagePos]);

	useEffect(() => {
		if (graph !== lastGraph.current) {
			panToNewDependency();
			lastGraph.current = graph;
		}
	}, [graph, panToNewDependency]);

	const issuesInGraph = useMemo(
		() =>
			(rollupBy !== 'team' &&
				itemIdsForGraph.map((issueId) => issuesById[issueId]).filter((issue) => issue)) ||
			[],
		[rollupBy, itemIdsForGraph, issuesById],
	);
	const showGraph = !(
		Object.keys(dependencyLinks).length > 0 &&
		(!Number.isFinite(layout.width) || !Number.isFinite(layout.height))
	);

	let stateScreen = null;

	let renderZoomBar = false;

	switch (status) {
		case ReportStatus.LOADING:
			stateScreen = <Spinner size="large" />;
			break;
		case ReportStatus.WARNING:
			stateScreen = <StateScreen type={ScreenType.WARNING} />;
			break;
		case ReportStatus.SUCCESS:
			if (!hasDependency) {
				stateScreen = <StateScreen type={ScreenType.EMPTY_NO_DEPENDENCY} />;
			} else if (hasDependency && Object.keys(dependencyLinks).length === 0) {
				// hasDependency is true when there are dependency in the plan
				// dependencyLinks might be empty due to the selected filters
				stateScreen = <StateScreen type={ScreenType.EMPTY_TOO_MANY_FILTERS} />;
			} else {
				// Only show zoom bar if no empty screen is rendered
				renderZoomBar = showGraph;
			}
			break;
		default:
			break;
	}

	const isTargetOutsideDetailFlyout = (e: MouseEvent<HTMLDivElement>) => {
		if (!currentDetailFlyoutId) return true;

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		const currentFlyout = document.getElementById(`detail-flyout-content-${currentDetailFlyoutId}`);
		return !(e.target instanceof Node && currentFlyout && currentFlyout.contains(e.target));
	};

	return (
		<UFOSegment name="advanced-roadmap-dependencies">
			<UFOLoadHold name="is-loading" hold={isLoading} />
			<div
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className={styles.dependenciesTab}
				data-testid="portfolio-3-portfolio.app-simple-plans.main.tabs.dependencies"
			>
				<ViewExperienceFailureTracker
					location="portfolio-3.simple-plans.dependencies.state-or-global-error"
					locationType="path"
					locationPrefix="dependencies"
				/>
				<MountEvent
					onMount={(analyticsEvent) => {
						const [actionSubject, action] =
							PRODUCT_ANALYTICS_EVENT_NAMES.VIEWED_DEPENDENCY_TAB.split(' ');
						fireUIAnalytics(analyticsEvent.update({ action, actionSubject }));
					}}
				/>
				<Filters
					issues={issuesInGraph}
					onFilterChange={() => {
						updateStagePos(0, 0, 1);
						updateWrapperHeight();
					}}
					onDependencyAdded={(id: string) => {
						setNewDependency(id);
					}}
					mode={mode}
					legendToggleButton={
						legendToggleButton ?? (isSmartLink && fg('smart_links_for_plans') && <Legend />)
					}
				/>
				{renderZoomBar && (
					<JiraBottomRightCornerOutlet
						orderFromRight={2}
						id="portfolio-3-plans-dependencies-overlay"
					>
						<ZoomBar
							onZoomIn={() => {
								zoom(getNextZoomStep(true));
							}}
							onZoomOut={() => {
								zoom(getNextZoomStep(false));
							}}
							onReset={() => {
								updateStagePos(0, 0, 1);
							}}
							onFit={() => {
								const pos = getFitPosition();
								updateStagePos(pos.x, pos.y, pos.scale);
							}}
							onMount={(setZoom) => {
								zoomBarZoomSetter.current = setZoom;
							}}
							initialScale={initialStagePosAndScale.scale}
						/>
					</JiraBottomRightCornerOutlet>
				)}
				{fg('smart_links_for_plans') ? <FilterQueryParams /> : <UseLocalStorageCommand />}
				{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
				<div className={stateScreen ? styles.wrapperStateScreen : styles.wrapper} ref={wrapperRef}>
					{stateScreen ||
						(showGraph && (
							<div
								role="button"
								tabIndex={0}
								// eslint-disable-next-line @typescript-eslint/no-empty-function
								onKeyDown={() => {}}
								style={{
									userSelect: shouldMoveStage.current ? 'none' : 'initial',
								}}
								onMouseDown={(e) => {
									const targetIsOutsideDetailFlyout = isTargetOutsideDetailFlyout(e);
									// The MouseEvent.button property indicates which button was pressed on the mouse to trigger the event
									// '2' is the secondary button pressed, usually the right button
									// so if the user right clicks on the card, we don't want the visualization to start moving around
									if (e && e.button !== 2 && targetIsOutsideDetailFlyout) {
										shouldMoveStage.current = true;
										if (wrapperRef.current) {
											wrapperRef.current.style.cursor = 'grabbing';
										}
									}
									if (targetIsOutsideDetailFlyout) setCurrentDetailFlyoutId(null);
								}}
								onMouseMove={(event) => {
									if (shouldMoveStage.current) {
										const x = stagePosAndScale.current.x + event.movementX;
										const y = stagePosAndScale.current.y + event.movementY;
										updateStagePos(x, y);
									}
								}}
								onMouseUp={() => {
									shouldMoveStage.current = false;
									if (wrapperRef.current) {
										wrapperRef.current.style.cursor = 'grab';
									}
								}}
								onClick={(e) => {
									focusIssueLink();
									if (isTargetOutsideDetailFlyout(e)) setCurrentDetailFlyoutId(null);
								}}
							>
								<div
									ref={containerRef}
									// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
									className={styles.container}
									style={{
										width: layout.width,
										height: layout.height,
										// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
										position: 'relative',
										transform: `translate3d(${stagePosAndScale.current.x}px, ${stagePosAndScale.current.y}px, 0) scale(${stagePosAndScale.current.scale})`,
									}}
									data-testid="portfolio-3-portfolio.app-simple-plans.main.tabs.dependencies.graph"
								>
									<Connections
										connections={graph
											.edges()
											.filter((edge) => labels[`${edge.v}:${edge.w}`])
											.map((edge) => ({
												edge: graph.edge(edge),
												link: labels[`${edge.v}:${edge.w}`],
												id: `${edge.v}:${edge.w}`,
											}))}
										size={{
											width: layout.width || 0,
											height: layout.height || 0,
										}}
										currentDetailFlyoutId={currentDetailFlyoutId}
										onLinkClick={(id, linksLength) => {
											onLineClick(linksLength);
											setCurrentDetailFlyoutId(id);
										}}
										onDeleteLink={() => {
											setCurrentDetailFlyoutId(null);
										}}
									/>
									{graph.nodes().map((nodeId) => {
										if (nodeId === OTHER_GROUP_ID) {
											return (
												<OtherIssue
													key={nodeId}
													position={{
														x: graph.node(nodeId).x,
														y: graph.node(nodeId).y,
													}}
													height={graph.node(nodeId).height}
													otherIssues={R.without(
														R.flatten(R.values(otherIssuesInGroupMap)),
														otherIssues,
													)}
													rollupBy={rollupBy}
												/>
											);
										}

										if (nodeId.endsWith(NO_PARENT_GROUP_SUFFIX)) {
											const groupKey = nodeId.split(':')[0];
											return (
												<OtherIssue
													key={nodeId}
													position={{
														x: graph.node(nodeId).x,
														y: graph.node(nodeId).y,
													}}
													height={graph.node(nodeId).height}
													otherIssues={otherIssuesInGroupMap[groupKey]}
													rollupBy={rollupBy}
												/>
											);
										}

										if (groupsOfIssues.has(nodeId)) {
											const groupId = getGroupIdFromGroupKey(nodeId, groupBy ?? '');
											switch (groupBy) {
												case GroupBy.TEAM:
													if (teamsById[groupId]) {
														return (
															<TeamCluster
																key={nodeId}
																position={{
																	x: graph.node(nodeId).x,
																	y: graph.node(nodeId).y,
																}}
																height={graph.node(nodeId).height}
																width={graph.node(nodeId).width}
																team={teamsById[groupId]}
															/>
														);
													}
													break;
												case GroupBy.PROJECT:
													if (projectsById[groupId]) {
														return (
															<ProjectCluster
																key={nodeId}
																position={{
																	x: graph.node(nodeId).x,
																	y: graph.node(nodeId).y,
																}}
																height={graph.node(nodeId).height}
																width={graph.node(nodeId).width}
																project={projectsById[groupId]}
															/>
														);
													}
													break;

												case GroupBy.EPIC_LEVEL:
													return (
														<EpicCluster
															key={nodeId}
															position={{
																x: graph.node(nodeId).x,
																y: graph.node(nodeId).y,
															}}
															height={graph.node(nodeId).height}
															width={graph.node(nodeId).width}
															issueId={groupId}
														/>
													);
												case GroupBy.SPRINT:
													if (sprintsById[groupId]) {
														return (
															<SprintClusterComponent
																key={nodeId}
																position={{
																	x: graph.node(nodeId).x,
																	y: graph.node(nodeId).y,
																}}
																height={graph.node(nodeId).height}
																width={graph.node(nodeId).width}
																sprint={sprintsById[groupId]}
															/>
														);
													}
													break;
												default:
													break;
											}
										}

										const item = rollupBy === 'team' ? teamsById[nodeId] : issuesById[nodeId];

										if (!item || !graph.node(nodeId)) {
											return null;
										}

										if (rollupBy === 'team') {
											return (
												<TeamView
													key={nodeId}
													itemId={nodeId}
													position={{
														x: graph.node(nodeId).x,
														y: graph.node(nodeId).y,
													}}
												/>
											);
										}

										return (
											<IssueView
												key={nodeId}
												itemId={nodeId}
												position={{
													x: graph.node(nodeId).x,
													y: graph.node(nodeId).y,
												}}
												focusIssueLink={focusIssueLink}
												isInFocus={activeIssue === item.id}
												onCardClick={onCardClick}
												setIssueFilter={setIssueFilter}
												onFilterByIssue={() => updateStagePos(0, 0, 1)}
											/>
										);
									})}
								</div>
							</div>
						))}
				</div>
			</div>
		</UFOSegment>
	);
}

export default Dependencies;
